/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentProps, RefObject } from 'preact';
import type { ListItemContext as ListItemMenuContext } from '#UNSAFE_Collection';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type { ListContextMenuConfig } from '../List.types';

import { useCallback, useRef, useState } from 'preact/hooks';
import { useContextMenuGesture } from '#hooks/UNSAFE_useContextMenuGesture';
import { Menu } from '#UNSAFE_Menu';
import { LIST_FOCUSABLE_SELECTOR, logicalCellExtractor } from '../utils/ListDomUtils';
import { getCellContextMenuContextFromCell } from '../utils/ListFocusUtils';

type MenuProps = Pick<
  ComponentProps<typeof Menu>,
  'anchorRef' | 'initialFocus' | 'placement' | 'offsetValue' | 'isOpen'
>;

const TOUCH_OFFSET_VALUE = 40;

const menuPropGestureStates: Record<
  'mouse' | 'keyboard' | 'touch',
  Omit<Required<MenuProps>, 'anchorRef' | 'isOpen'>
> = {
  mouse: {
    initialFocus: 'menu',
    placement: 'bottom-start',
    offsetValue: 2
  },
  keyboard: {
    initialFocus: 'firstItem',
    placement: 'bottom-start',
    offsetValue: 0
  },
  touch: {
    initialFocus: 'menu',
    placement: 'end',
    offsetValue: TOUCH_OFFSET_VALUE
  }
};

const _initialMenuProps: MenuProps = {
  isOpen: false,
  initialFocus: 'menu',
  placement: 'bottom-start',
  offsetValue: 0,
  anchorRef: { current: null }
};

export const useContextMenu = <K extends string | number, D>(
  rootRef: RefObject<HTMLDivElement>,
  listId: string,
  listData: CollectionData<K, D>[],
  contextMenuConfig?: ListContextMenuConfig<K, D>
) => {
  const [contextMenuContext, setContextMenuContext] = useState<ListItemMenuContext<K, D>>();
  const [menuProps, setMenuProps] = useState<MenuProps>(_initialMenuProps);
  const currentCellElementRef = useRef<HTMLElement>();

  const { triggerProps } = useContextMenuGesture(
    ({ gesture, anchor, target }) => {
      if (rootRef.current) {
        // find the cell element the menu is being launched from
        const cellElement = (target as HTMLElement).closest(
          `[${LIST_FOCUSABLE_SELECTOR}='${listId}']`
        ) as HTMLElement;
        if (cellElement != null) {
          const targetCell = logicalCellExtractor<K>(cellElement, listId);
          if (targetCell != null) {
            const cellContextMenuContext = getCellContextMenuContextFromCell<K, D>(
              rootRef.current,
              listId,
              listData,
              targetCell
            );
            if (cellContextMenuContext != null) {
              currentCellElementRef.current = cellElement;
              const anchorBasedOnGesture = gesture === 'keyboard' ? cellElement : anchor;
              setContextMenuContext(cellContextMenuContext);
              setMenuProps({
                ...menuPropGestureStates[gesture],
                anchorRef: { current: anchorBasedOnGesture },
                isOpen: true
              });
            }
          }
        }
      }
    },
    { isDisabled: !contextMenuConfig }
  );

  const handleCloseContextMenu = useCallback<Required<ComponentProps<typeof Menu>>['onClose']>(
    (detail) => {
      // set focus back on the cell that was determined as the initial target
      if (detail.reason === 'dismissed' || detail.reason === 'itemAction') {
        currentCellElementRef.current?.focus({ preventScroll: true });
      }
      // close the menu
      setMenuProps({ ..._initialMenuProps });
    },
    []
  );

  return {
    menuProps: { ...menuProps, onClose: handleCloseContextMenu },
    contextMenuContext: contextMenuContext,
    contextMenuTriggerProps: triggerProps
  };
};
